import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
  IconButton,
  Tabs,
  Tab
} from '@mui/material';
import {
  ArrowBack,
  Visibility,
  CheckCircle,
  Cancel,
  AccessTime
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const P2PHistory = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDarkMode = theme.palette.mode === 'dark';
  
  const [activeTab, setActiveTab] = useState(0);
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Get orders from unified user data
  const userData = useSelector((state) => state.userData?.user);
  const loading = useSelector((state) => state.userData?.loading || false);
  
  // Get P2P orders from user data
  const orders = userData?.p2p_orders || [];
  
  // Debug logging
  console.log('P2PHistory Debug:', {
    userData,
    orders,
    ordersLength: orders.length,
    firstOrder: orders[0],
    firstOrderType: orders[0]?.type,
    firstOrderStatus: orders[0]?.status
  });

  // Filter orders based on active tab
  const filteredOrders = orders.filter(order => {
    if (activeTab === 0) return true; // All orders
    if (activeTab === 1) return order.status === 'completed'; // Completed
    if (activeTab === 2) return ['payment_pending', 'payment_confirmed'].includes(order.status); // In Progress
    if (activeTab === 3) return ['cancelled', 'expired'].includes(order.status); // Cancelled
    return true;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'payment_pending':
        return 'warning';
      case 'payment_confirmed':
        return 'info';
      case 'completed':
        return 'success';
      case 'cancelled':
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'payment_pending':
        return 'Pending Payment';
      case 'payment_confirmed':
        return 'Payment Confirmed';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      case 'expired':
        return 'Expired';
      default:
        return status;
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    // Handle NaN or undefined amounts
    if (!amount || isNaN(amount) || amount === 'NaN') {
      return 'N/A';
    }
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(parseFloat(amount));
  };

  const formatTime = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(date));
  };

  const handleViewOrder = (orderId) => {
    navigate(`/p2p/transaction/${orderId}`);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate('/p2p')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" fontWeight="bold">
            P2P Order History
          </Typography>
        </Box>

        {/* Filter Tabs */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ pb: 1 }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              variant={isMobile ? 'scrollable' : 'standard'}
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500
                }
              }}
            >
              <Tab label="All Orders" />
              <Tab label="Completed" />
              <Tab label="In Progress" />
              <Tab label="Cancelled" />
            </Tabs>
          </CardContent>
        </Card>

        {/* Orders Table/Cards */}
        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography>Loading order history...</Typography>
              </Box>
            ) : filteredOrders.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  No orders found for the selected filter
                </Typography>
                <Button 
                  variant="contained" 
                  sx={{ mt: 2 }}
                  onClick={() => navigate('/p2p')}
                >
                  Start Trading
                </Button>
              </Box>
            ) : isMobile ? (
              // Mobile Card View
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {filteredOrders.map((order) => (
                  <Card 
                    key={order.id} 
                    variant="outlined"
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { boxShadow: 2 }
                    }}
                    onClick={() => handleViewOrder(order.id)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" fontWeight="bold">
                          {order.id}
                        </Typography>
                        <Chip
                          label={getStatusText(order.status)}
                          color={getStatusColor(order.status)}
                          size="small"
                        />
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Type</Typography>
                          <Chip
                            label={order.type?.toUpperCase() || 'N/A'}
                            color={order.type === 'buy' ? 'success' : 'error'}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Amount</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(order.fiat_amount, order.fiat_currency)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Partner</Typography>
                          <Typography variant="body2">{order.partner.name}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Time</Typography>
                          <Typography variant="body2">{formatTime(order.created_at)}</Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              // Desktop Table View
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: isDarkMode ? 'rgba(247, 147, 26, 0.1)' : 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Order ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Crypto Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Partner</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredOrders.map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {order.id || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={order.type?.toUpperCase() || 'N/A'}
                            color={order.type === 'buy' ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(order.fiat_amount, order.fiat_currency)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {order.crypto_amount} {order.cryptocurrency}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar 
                              src={order.partner?.avatar} 
                              sx={{ width: 24, height: 24 }}
                            />
                            <Typography variant="body2">
                              {order.partner?.name || 'Unknown Partner'}
                            </Typography>
                            {order.partner?.is_verified && (
                              <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusText(order.status || 'unknown')}
                            color={getStatusColor(order.status || 'unknown')}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatTime(order.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton 
                            size="small" 
                            onClick={() => handleViewOrder(order.id)}
                          >
                            <Visibility />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </Container>
  );
};

export default P2PHistory;
